// components
import CloseIcon from '@mui/icons-material/Close';
import Dialog, { DialogProps } from '@mui/material/Dialog';
import MuiDialogContent, {
  DialogContentProps as MUIDialogContentProps
} from '@mui/material/DialogContent';
import MuiDialogTitle, { DialogTitleProps as MUIDialogTitleProps } from '@mui/material/DialogTitle';
import { IconButton, Theme, Typography, DialogActions } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FC } from 'react';
import clsx from 'clsx';
import { ModalTitle } from './modal-title';

interface IModalProps extends DialogProps {
  DialogContentProps?: MUIDialogContentProps;
  DialogTitleProps?: MUIDialogTitleProps;
  subtitle?: string;
  title?: string;
  actions?: JSX.Element;
  noPaddingContent?: boolean;
  showClose?: boolean;
  isLoading?: boolean; // Hides modal title/close button while true
  titleMarginBottom?: number;
}

export const Modal: FC<IModalProps> = ({
  DialogContentProps,
  DialogTitleProps,
  subtitle,
  title,
  actions,
  children,
  noPaddingContent,
  showClose = true,
  isLoading = false,
  titleMarginBottom,
  ...dialogProps
}) => {
  const classes = modalStyles({ noPaddingContent });
  return (
    <Dialog fullWidth={true} maxWidth="xl" {...dialogProps}>
      {!isLoading && (
        <MuiDialogTitle classes={{ root: classes.dialogTitle }} {...DialogTitleProps}>
          {title && <ModalTitle title={title} titleMarginBottom={titleMarginBottom} />}
          {showClose && (
            <IconButton
              aria-label="Close"
              className={clsx(classes.close, 'print--none')}
              onClick={() => {
                if (dialogProps && dialogProps.onClose) {
                  dialogProps.onClose({}, 'backdropClick');
                }
              }}
              size="large"
            >
              <CloseIcon />
            </IconButton>
          )}
        </MuiDialogTitle>
      )}
      <MuiDialogContent
        classes={{ root: classes.dialogContent }}
        dividers={false}
        {...DialogContentProps}
      >
        {subtitle && (
          <Typography className={classes.subtitle} variant="h4">
            {subtitle}
          </Typography>
        )}
        {children}
      </MuiDialogContent>
      {actions && <DialogActions>{actions}</DialogActions>}
    </Dialog>
  );
};

const modalStyles = makeStyles<Theme, { noPaddingContent?: boolean }>((theme: Theme) => {
  return {
    close: {
      position: 'absolute',
      right: theme.spacing(0.25),
      top: theme.spacing(0.25),
      color: theme.palette.grey[600]
    },
    dialogContent: {
      // minHeight is to give <Loader /> component top and bottom space, if shown with no content to overlay
      minHeight: ({ noPaddingContent }) => (noPaddingContent ? 'auto' : theme.spacing(4)),
      padding: ({ noPaddingContent }) => (noPaddingContent ? 0 : theme.spacing(0.5, 1.5, 1.5)),
      '@media print': {
        padding: 0,
        margin: 0
      }
    },
    dialogTitle: {
      margin: 0,
      padding: ({ noPaddingContent }) => (noPaddingContent ? 0 : theme.spacing(2, 1.5, 0, 1.5)),
      '@media print': {
        padding: 0,
        margin: 0,
        display: 'none'
      }
    },
    subtitle: {
      color: theme.palette.secondary.main,
      fontSize: 28,
      lineHeight: 1.3,
      margin: theme.spacing(0, 0, 1)
    }
  };
});
